import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';

// ---------------------------------------------------------------------------
// Notification types — must stay in sync with iOS UserNotification.NotificationType
// ---------------------------------------------------------------------------
export const NOTIFICATION_TYPES = {
  POST_LIKED:          'postLiked',
  POST_COMMENTED:      'postCommented',
  COMMENT_LIKED:       'commentLiked',
  COMMENT_REPLIED:     'commentReplied',
  RECIPE_LIKED:        'recipeLiked',
  RECIPE_COMMENTED:    'recipeCommented',
  COMMUNITY_APPROVED:  'communityApproved',
  COMMUNITY_REJECTED:  'communityRejected',
  MENTIONED:           'mentioned',
  ANNOUNCEMENT:        'announcement',
};

// ---------------------------------------------------------------------------
// Default preferences — mirrors iOS NotificationPreferences defaults (all true)
// Stored at users/{uid}.notificationPreferences
// ---------------------------------------------------------------------------
const DEFAULT_PREFERENCES = {
  postLikes:          true,
  postComments:       true,
  commentLikes:       true,
  commentReplies:     true,
  recipeLikes:        true,
  recipeComments:     true,
  communityApprovals: true,
  mentions:           true,
  announcements:      true,
};

// Map notification type string to preference key
const PREFERENCE_KEY_MAP = {
  [NOTIFICATION_TYPES.POST_LIKED]:         'postLikes',
  [NOTIFICATION_TYPES.POST_COMMENTED]:     'postComments',
  [NOTIFICATION_TYPES.COMMENT_LIKED]:      'commentLikes',
  [NOTIFICATION_TYPES.COMMENT_REPLIED]:    'commentReplies',
  [NOTIFICATION_TYPES.RECIPE_LIKED]:       'recipeLikes',
  [NOTIFICATION_TYPES.RECIPE_COMMENTED]:   'recipeComments',
  [NOTIFICATION_TYPES.COMMUNITY_APPROVED]: 'communityApprovals',
  [NOTIFICATION_TYPES.COMMUNITY_REJECTED]: 'communityApprovals',
  [NOTIFICATION_TYPES.MENTIONED]:          'mentions',
  [NOTIFICATION_TYPES.ANNOUNCEMENT]:       'announcements',
};

// ---------------------------------------------------------------------------
// useNotifications
// Real-time listener on notifications collection for the current user.
// Exposes read, mark-as-read, delete, and preference management.
// ---------------------------------------------------------------------------
export const useNotifications = (fetchLimit = 50) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);

  // Real-time listener
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('timestamp', 'desc'),
      limit(fetchLimit)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate?.() ?? d.data().timestamp ?? null,
      }));
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.isRead).length);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, fetchLimit]);

  // Load preferences on mount
  useEffect(() => {
    if (!user) return;

    const fetchPreferences = async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const prefs = userSnap.data()?.notificationPreferences;
        if (prefs) {
          setPreferences({ ...DEFAULT_PREFERENCES, ...prefs });
        }
      } catch (err) {
        console.error('Error fetching notification preferences:', err);
      }
    };

    fetchPreferences();
  }, [user]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), { isRead: true });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    try {
      const unread = notifications.filter(n => !n.isRead);
      if (unread.length === 0) return;
      const batch = writeBatch(db);
      unread.forEach(n => {
        batch.update(doc(db, 'notifications', n.id), { isRead: true });
      });
      await batch.commit();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [user, notifications]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, []);

  const updatePreferences = useCallback(async (updatedPrefs) => {
    if (!user) return;
    try {
      const merged = { ...preferences, ...updatedPrefs };
      await updateDoc(doc(db, 'users', user.uid), {
        notificationPreferences: merged,
      });
      setPreferences(merged);
    } catch (err) {
      console.error('Error updating notification preferences:', err);
    }
  }, [user, preferences]);

  return {
    notifications,
    unreadCount,
    preferences,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updatePreferences,
  };
};

// ---------------------------------------------------------------------------
// sendNotification
// Standalone async function — called from action hooks, not UI components.
// Checks recipient preferences before writing.
// Never fires when actorUserId === recipientUserId.
// Matches iOS NotificationRepository.createNotification logic exactly.
// ---------------------------------------------------------------------------
export const sendNotification = async ({
  recipientUserId,
  type,
  title,
  message,
  actorUserId = null,
  actorUserName = null,
  targetId = null,
  targetType = null,
  communityId = null,
  communityName = null,
}) => {
  // Never notify a user about their own action
  if (actorUserId && actorUserId === recipientUserId) return;

  try {
    // Check recipient preferences
    const userSnap = await getDoc(doc(db, 'users', recipientUserId));
    const prefs = {
      ...DEFAULT_PREFERENCES,
      ...(userSnap.data()?.notificationPreferences ?? {}),
    };

    const prefKey = PREFERENCE_KEY_MAP[type];
    if (prefKey && prefs[prefKey] === false) return;

    await addDoc(collection(db, 'notifications'), {
      userId: recipientUserId,
      type,
      title,
      message,
      actorUserId,
      actorUserName,
      targetId,
      targetType,
      communityId,
      communityName,
      isRead: false,
      timestamp: new Date(),
    });
  } catch (err) {
    // Notification failure must never break the calling action
    console.error('Error sending notification:', err);
  }
};

// ---------------------------------------------------------------------------
// Convenience senders — mirror iOS NotificationRepository extension methods
// ---------------------------------------------------------------------------

export const notifyPostLiked = ({ postOwnerId, postId, actorUserId, actorUserName, communityName }) =>
  sendNotification({
    recipientUserId: postOwnerId,
    type: NOTIFICATION_TYPES.POST_LIKED,
    title: 'New Like',
    message: `${actorUserName} liked your post`,
    actorUserId,
    actorUserName,
    targetId: postId,
    targetType: 'post',
    communityName: communityName ?? null,
  });

export const notifyPostCommented = ({ postOwnerId, postId, actorUserId, actorUserName, commentPreview, communityName }) =>
  sendNotification({
    recipientUserId: postOwnerId,
    type: NOTIFICATION_TYPES.POST_COMMENTED,
    title: 'New Comment',
    message: `${actorUserName} commented: ${commentPreview}`,
    actorUserId,
    actorUserName,
    targetId: postId,
    targetType: 'post',
    communityName: communityName ?? null,
  });

export const notifyCommentLiked = ({ commentOwnerId, commentId, actorUserId, actorUserName }) =>
  sendNotification({
    recipientUserId: commentOwnerId,
    type: NOTIFICATION_TYPES.COMMENT_LIKED,
    title: 'New Like',
    message: `${actorUserName} liked your comment`,
    actorUserId,
    actorUserName,
    targetId: commentId,
    targetType: 'comment',
  });

export const notifyCommentReplied = ({ commentOwnerId, commentId, actorUserId, actorUserName, replyPreview }) =>
  sendNotification({
    recipientUserId: commentOwnerId,
    type: NOTIFICATION_TYPES.COMMENT_REPLIED,
    title: 'New Reply',
    message: `${actorUserName} replied: ${replyPreview}`,
    actorUserId,
    actorUserName,
    targetId: commentId,
    targetType: 'comment',
  });

export const notifyCommunityApproved = ({ userId, communityId, communityName }) =>
  sendNotification({
    recipientUserId: userId,
    type: NOTIFICATION_TYPES.COMMUNITY_APPROVED,
    title: 'Community Approved',
    message: `Your community "${communityName}" has been approved and is now live.`,
    targetId: communityId,
    targetType: 'community',
    communityId,
    communityName,
  });

export const notifyCommunityRejected = ({ userId, communityId, communityName, reason }) =>
  sendNotification({
    recipientUserId: userId,
    type: NOTIFICATION_TYPES.COMMUNITY_REJECTED,
    title: 'Community Not Approved',
    message: `Your community "${communityName}" was not approved. Reason: ${reason}`,
    targetId: communityId,
    targetType: 'community',
    communityId,
    communityName,
  });
